import { AfterViewInit, ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { PointFeature } from '@shared/models/point';
import { SegmentFeature } from '@shared/models/segment';
import { MapControlsComponent } from './map-controls/map-controls.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { AuthService } from '@shared/auth/auth.service';
import { MapDataService } from '@services/map-data.service';
import { MarkerContentService } from '@services/marker-content.service';
import { environment } from '@env/environment';
import { DeleteDialogComponent } from '@shared/components/delete-dialog/delete-dialog.component';
import { MessageService } from '@services/message.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, throttleTime } from 'rxjs';
import { Region } from '@models/region';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [
    GoogleMap,
    MapControlsComponent,
    MatSidenavModule,
    MatButtonModule,
    MatDialogModule
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent {
  @ViewChild(GoogleMap) wallMap!: GoogleMap;
  private map!: google.maps.Map;
  private mapBoundsChanged = new Subject<google.maps.LatLngBoundsLiteral>();
  public mapZoom: number = 18;
  public maxZoom!: number;
  public zoomService!: google.maps.MaxZoomService;
  public drawingManager!: google.maps.drawing.DrawingManager;
  public points: PointFeature[] = [];
  public segments: SegmentFeature[] = [];
  public regions: Region[] = [];
  public drawingMode: DrawingModes = DrawingModes.NONE;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  readonly deleteDialog = inject(MatDialog);
  private messageService = inject(MessageService);

  constructor(
    private auth: AuthService,
    private mapData: MapDataService,
    public markerContent: MarkerContentService
  ) {}

  async setMapOptions() : Promise<void> {
    // need this to make sure map is loaded, maybe there is a better way
    await google.maps.importLibrary("maps");
    const { DrawingManager } = await google.maps.importLibrary("drawing") as google.maps.DrawingLibrary;

    let options: google.maps.MapOptions = {
      center: { lat: environment.home.coordinates[1], lng: environment.home.coordinates[0] },
      zoom: this.mapZoom,
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      disableDefaultUI: true,
      mapId: "DEMO_MAP_ID",
      gestureHandling: "greedy",
    };
    this.map = this.wallMap.googleMap!;
    this.map.setOptions(options);
    this.drawingManager = new DrawingManager({ map: this.map, drawingControl: false });
    this.zoomService = new google.maps.MaxZoomService();
    this.setMaxZoom();

    // Add listeners
    google.maps.event.addListener(this.map, 'center_changed', () => {
      this.setMaxZoom();
    });
    google.maps.event.addListener(this.map, 'zoom_changed', () => {
      this.mapZoom = this.map.getZoom()!;
    });
    google.maps.event.addListener(this.map, 'bounds_changed', () => {
      this.onMapBoundsChange();
    });
    google.maps.event.addListener(this.drawingManager, 'markercomplete', (overlay: google.maps.Marker) => {
      this.addPoint(overlay);
    });
    google.maps.event.addListener(this.drawingManager, 'polylinecomplete', (overlay: google.maps.Polyline) => {
      this.addSegment(overlay);
    });
    google.maps.event.addListener(this.drawingManager, 'polygoncomplete', (overlay: google.maps.Polygon) => {
      this.addRegion(overlay);
    });
  }

  public setCenter(lat: number, lon: number) {
    this.map.setCenter(new google.maps.LatLng(lat, lon));
  }

  public setZoom(zoom: number) {
    if (zoom > this.maxZoom) { this.mapZoom = this.maxZoom; }
    else if (zoom < 0) { this.mapZoom = 0; }
    else { this.mapZoom = zoom; }
    this.map.setZoom(this.mapZoom);
  }

  public getCenter() {
    return this.map.getCenter();
  }

  public onMapBoundsChange() {
    if (!this.mapBoundsChanged.observed) {
      this.mapBoundsChanged.pipe(throttleTime(800)).subscribe(
        (bounds) => {
          this.setPoints(bounds);
          this.setSegments(bounds);
          this.setRegions(bounds);
        }
      );
    }
    this.mapBoundsChanged.next(this.map.getBounds()!.toJSON());
  }

  public setDrawingMode(mode: DrawingModes) {
    // let OverlayType = google.maps.drawing.OverlayType;
    switch (mode) {
      case DrawingModes.CIRCLE:
        this.drawingMode = DrawingModes.CIRCLE;
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
        break;
      case DrawingModes.MARKER:
        this.drawingMode = DrawingModes.MARKER;
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.MARKER);
        break;
      case DrawingModes.POLYGON:
        this.drawingMode = DrawingModes.POLYGON;
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
        break;
      case DrawingModes.POLYLINE:
        this.drawingMode = DrawingModes.POLYLINE;
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYLINE);
        break;
      case DrawingModes.RECTANGLE:
        this.drawingMode = DrawingModes.RECTANGLE;
        this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE);
        break;
      case DrawingModes.EDIT:
        this.drawingMode = DrawingModes.EDIT;
        this.drawingManager.setDrawingMode(null);
        break;
      case DrawingModes.DELETE:
        this.drawingMode = DrawingModes.DELETE;
        this.drawingManager.setDrawingMode(null);
        break;
      case DrawingModes.NONE:
        this.drawingMode = DrawingModes.NONE;
        this.drawingManager.setDrawingMode(null);
        break;
    }
  }

  private setMaxZoom() {
    let self = this;
    this.zoomService.getMaxZoomAtLatLng(this.wallMap.getCenter()!, function (response) {
      self.maxZoom = response.zoom;
    });
  }

  // Can I move this to the service and return the array?
  private setPoints(bounds?: google.maps.LatLngBoundsLiteral) {
    this.mapData.getFeatures('Point', bounds).subscribe({
      next: (data) => {
        data.features.forEach(element => {
          let point = new PointFeature(element);
          if (this.points[point.id]) { 
            // the point is already in the array
            // check if the newly fetched version has changed data
            const curPoint = this.points[point.id];
            point.hasChanges = !(
              point.name === curPoint.name &&
              point.description === curPoint.description &&
              point.coordinates[0] === curPoint.coordinates[0] &&
              point.coordinates[1] === curPoint.coordinates[1] &&
              point.color === curPoint.color &&
              point.images.length === curPoint.images.length &&
              point.images.every((val, index) => val.equals(curPoint.images[index]))
            );
          }
          // replace the point or newly add it, mostly just want to avoid redrawing markers when possible
          this.points[point.id] = point;
        });
        this.renderPoints();
      }
    });
  }

  private setSegments(bounds?: google.maps.LatLngBoundsLiteral) {
    this.mapData.getFeatures('Segment', bounds).subscribe({
      next: (data) => {
        data.features.forEach(element => {
          let segment = new SegmentFeature(element);
          // segment.setPoints(element.properties?.points);
          this.segments[segment.id] = segment;
        });
        this.renderSegments();
      }
    });
  }

  private setRegions(bounds?: google.maps.LatLngBoundsLiteral) {
    this.mapData.getFeatures('Region', bounds).subscribe({
      next: (data) => {
        data.features.forEach(element => {
          let region = new Region(element.geometry.coordinates, element.properties?.name, element.properties?.description, [], element.properties?.color);
          region.id = element.id!;
          this.regions[region.id] = region;
        });
        this.renderRegions();
      }
    })
  }

  private async renderPoints() {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    this.points.forEach(element => {
      if (this.markers[element.id] && element.hasChanges) {
        // there is a marker that needs to be removed and redrawn with new content
        this.markers[element.id].remove();
        this.markers[element.id].map = null;
        this.markers.splice(element.id, 1);
        this.renderPoint(element);
      } else if (!this.markers[element.id]) {
        // if the marker is not yet in markers[] then render it
        this.renderPoint(element);
      } // else we don't do anything
    });
  }

  private renderPoint(point: PointFeature) {
    let marker = new google.maps.marker.AdvancedMarkerElement({
      position: {lat: point.coordinates[1], lng: point.coordinates[0]},
      map: this.map,
      content: this.markerContent.getAsElement(point),
      gmpClickable: true
    });
    this.markers[point.id] = marker;
    google.maps.event.addListener(marker, 'click', () => this.onPointClick(point, marker));
  }

  private renderSegments() {
    this.segments.forEach(element => {
      this.renderSegment(element);
    });
  }

  private renderSegment (segment: SegmentFeature) {
    let coordinates: {lat: number, lng: number}[] = [];
      segment.coordinates.forEach((coordinate: [number, number]) => {
        coordinates.push({lat: coordinate[1], lng: coordinate[0]});
      });
      const polyline = new google.maps.Polyline({
        path: coordinates,
        geodesic: true
      });
      polyline.setMap(this.map);
      google.maps.event.addListener(polyline, 'click', () => this.onSegmentClick(segment, polyline));
      google.maps.event.addListener(polyline, 'mouseover', () => {
        polyline.setOptions({strokeWeight: 4});
      });
      google.maps.event.addListener(polyline, 'mouseout', () => {
        polyline.setOptions({strokeWeight: 2});
      });
  }

  private renderRegions() {
    this.regions.forEach(element => {
      this.renderRegion(element);
    });
  }

  private renderRegion(region: Region) {
    let coordinates: {lat: number, lng: number}[] = [];
    let revCoordinates: {lat: number, lng: number}[] = [];
    region.coordinates[0].forEach(coordinate => {
      coordinates.push({lat: coordinate[1], lng: coordinate[0]});
      revCoordinates.push({lat: coordinate[1], lng: coordinate[0]});
    });

    const polygon = new google.maps.Polygon({
      paths: [coordinates, revCoordinates.reverse()],
      fillOpacity: 0,
      strokeColor: region?.color || '#0000FF'
    });
    polygon.setMap(this.map);
    polygon.addListener('click', (e: google.maps.MapMouseEvent) => this.onRegionClick(region, polygon, e.latLng!));
    google.maps.event.addListener(polygon, 'mouseover', () => {
      polygon.setOptions({strokeWeight: 4});
    });
    google.maps.event.addListener(polygon, 'mouseout', () => {
      polygon.setOptions({strokeWeight: 2});
    });
  }

  private addPoint(marker: google.maps.Marker) {
    const point = new PointFeature({geometry: {coordinates: [marker.getPosition()?.lng(), marker.getPosition()?.lat()]}});
    this.mapData.createFeature(point.toJson()).subscribe({
      next: (data) => {
        point.id = data.id!;
        this.points[point.id] = point;
        this.renderPoint(point);
        marker.setMap(null);
        this.messageService.openSnackBar('Added new point to the map!');
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.messageService.openSnackBar('You must be logged in to add points to the map');
        }
      }
    });
  }

  // Adjust to not be concerned with bulk add
  private addSegment(polyline: google.maps.Polyline) {
    let newCoordinates: [number, number][] = [];
    polyline.getPath().getArray().forEach(function(coordinate) {
      let coordinates = coordinate.toJSON();
      newCoordinates.push([coordinates.lng, coordinates.lat]);
    });
    const segment = new SegmentFeature({geometry: { coordinates: newCoordinates } });
    this.mapData.createFeature(segment.toJson()).subscribe({
      next: (data) => {
        segment.id = data.id!;
        this.segments[segment.id] = segment;
        this.renderSegment(segment);
        polyline.setMap(null);
        this.messageService.openSnackBar('Added new segment to the map!');
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.messageService.openSnackBar('You must be logged in to add segments to the map');
        }
      }
    });
  }

  private addRegion(polygon: google.maps.Polygon) {
    let newCoordinates: [number, number][] = [];
    polygon.getPath().getArray().forEach(coordinate => {
      newCoordinates.push([coordinate.lng(), coordinate.lat()])
    });
    // region has to end with same point as it begins with
    newCoordinates.push(newCoordinates[0]);
    const region = new Region([newCoordinates]);
    this.mapData.createFeature(region.toJson()).subscribe({
      next: (data) => {
        region.id = data.id!;
        this.regions[region.id] = region;
        this.renderRegion(region);
        polygon.setMap(null);
        this.messageService.openSnackBar('Added new region to the map!');
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.messageService.openSnackBar('You must be logged in to add regions to the map');
        }
      }
    });
  }

  private onPointClick(point: PointFeature, marker: google.maps.marker.AdvancedMarkerElement) {
    if (this.drawingMode === DrawingModes.DELETE) {
      const dialogRef = this.deleteDialog.open(DeleteDialogComponent);
      
      dialogRef.afterClosed().subscribe(res => {
       if (res) {
        this.deletePoint(point.id!, marker);
       } else {
       }   
      });
    } else if (this.drawingMode === DrawingModes.EDIT) {
      marker.draggable = true;
    } else {
    }
  }

  private onSegmentClick(segment: SegmentFeature, polyline: google.maps.Polyline) {
    if (this.drawingMode === DrawingModes.DELETE) {
      const dialogRef = this.deleteDialog.open(DeleteDialogComponent);
      
      dialogRef.afterClosed().subscribe(res => {
       if (res) {
        this.deleteSegment(segment.id!, polyline);
       } else {
       }   
      });
    } else if (this.drawingMode === DrawingModes.EDIT) {
      polyline.setEditable(true);
    } else {
    }
  }

  private onRegionClick(region: Region, polygon: google.maps.Polygon, clickLocation: google.maps.LatLng) {
    if (this.drawingMode === DrawingModes.DELETE) {
      const dialogRef = this.deleteDialog.open(DeleteDialogComponent);
      
      dialogRef.afterClosed().subscribe(res => {
       if (res) {
        this.deleteRegion(region.id!, polygon);
       } else {
       }   
      });
    } else if (this.drawingMode === DrawingModes.EDIT) {
      polygon.setEditable(true);
    } else {
      let infoWindow = new google.maps.InfoWindow({
        content: `<span class="region-info"><p>${region.name}</p><p>${region.description}</p></span>`,
        position: clickLocation,
        maxWidth: 275
      });
      infoWindow.open({
        map: this.map
      });
    }
  }

  private deletePoint(id: number, marker: google.maps.marker.AdvancedMarkerElement) {
    this.mapData.deleteFeature('Point', id).subscribe(() => {
      this.points.splice(id, 1);
      marker.map = null;
      this.messageService.openSnackBar('Successfully deleted point.');
    });
  }

  private deleteSegment(id: number, polyline: google.maps.Polyline) {
    this.mapData.deleteFeature('Segment', id).subscribe(() => {
      this.segments.splice(id, 1);
      polyline.setMap(null);
      this.messageService.openSnackBar('Successfully deleted segment.');
    });
  }

  private deleteRegion(id: number, polygon: google.maps.Polygon) {
    this.mapData.deleteFeature('Region', id).subscribe(() => {
      this.regions.splice(id, 1);
      polygon.setMap(null);
      polygon.setVisible(false);
      this.messageService.openSnackBar('Successfully deleted region.');
    });
  }
}

export enum DrawingModes {
  CIRCLE = "circle",
  MARKER = "marker",
  POLYGON = "polygon",
  POLYLINE = "polyline",
  RECTANGLE = "rectangle",
  DELETE = "delete",
  EDIT = "edit",
  NONE = "none"
}
